
import type { PropType } from 'vue'
import { defineComponent, watch, reactive } from 'vue'

import Prices from './Prices.vue'
import Pay from './Pay.vue'
import History from './History.vue'

// > services/types <
import { PaymentRequest, WaybillByPaymentRequest } from '@/modern/modules/m-accounting-module/services/types/payment-requests.types'

// > services/waybills
import paymentRequestsAPI from '@/modern/modules/m-accounting-module/services/payment-requests.service'

import type { FullFTLOrderT } from '@/core/types/orders.types'
import { orders } from '@/core/realisations/orders.realisation'

// > helpers/formatDate <
import formatDate from '@/modern/helpers/formatDate'

// > helpers/formatPriceToTenge <
import formatPriceToTenge from '@/modern/helpers/formatPriceToTenge'

export default defineComponent({
    components: { Prices, Pay, History },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: Boolean,
            default: false,
        },
        paymentRequest: {
            type: Object as PropType<PaymentRequest | null>,
            default: null
        }
    },
    emits: ['update:modelValue', 'close', 'pay', 'decline'],
    setup(props, { emit }) {
        const state = reactive({
            isLoading: false,
            isFailed: false,
            details: null as WaybillByPaymentRequest | null,
            ftlOrder: null as FullFTLOrderT | null,
        })

        const onClose = () => {
            if (props.disabled) return
            emit('close')
        }

        const onUpdateModelValue = (newValue: boolean) => {
            if (props.disabled) return
            emit('update:modelValue', newValue)
        }

        const loadWaybillDetails = async () => {
            if (!props.paymentRequest) return

            try {
                state.isLoading = true

                const response = await paymentRequestsAPI.getWaybillByPaymentRequest(props.paymentRequest.id)
                state.details = response.data.data

                state.isFailed = false
            } catch (error) {
                console.error(error)
                state.isFailed = true
            } finally {
                state.isLoading = false
            }
        }

        const loadFtlOrder = async () => {
            if (!props.paymentRequest) return

            try {
                state.isLoading = true

                const response = await orders.getFTLOrder(props.paymentRequest.clientId)
                state.ftlOrder = response.data

                state.isFailed = false
            } catch (error) {
                console.error(error)
                state.isFailed = true
            } finally {
                state.isLoading = false
            }
        }

        const getColor = () => {
            if (props.paymentRequest) {
                if (props.paymentRequest.status) {
                    if (props.paymentRequest.status.id === 60) {
                        return 'green'
                    }

                    if (props.paymentRequest.status.id === 61) {
                        return 'red'
                    }

                    if (props.paymentRequest.status.id === 62) {
                        return 'orange'
                    }
                }
            }

            return 'gray'
        }

        const formatTimeFromISO = (isoString: string): string => {
            const date = new Date(isoString)
            
            const hours = date.getHours()
            const minutes = date.getMinutes()
            
            const formattedHours = hours.toString().padStart(2, '0')
            const formattedMinutes = minutes.toString().padStart(2, '0')
            
            return `${formattedHours}:${formattedMinutes}`
        }
        
        const onPay = () => {
            emit('pay', props.paymentRequest)
        }

        const onDecline = () => {
            emit('decline', props.paymentRequest)
        }

        const loadData = () => {
            if (props.paymentRequest) {
                if (props.paymentRequest.clientType === 'Фтл заказ') {
                    loadFtlOrder()
                } else {
                    loadWaybillDetails()
                }
            }
        }

        watch(
            () => props.modelValue,
            (newValue) => {
                if (newValue) {
                    loadData()
                } else {
                    state.isFailed = false
                    state.isLoading = false
                    state.details = null
                    state.ftlOrder = null
                }
            }
        )

        return {
            formatDate,
            formatPriceToTenge,
            state,
            onClose,
            onUpdateModelValue,
            loadData,
            getColor,
            formatTimeFromISO,
            onPay,
            onDecline
        }
    }
})
